import * as React from 'react';
import * as prospectApi from '../../../../api/Prospect';
import { useState, useEffect } from 'react';
import { DashboardPanel } from '../DashboardPanel';
import * as moment from 'moment';
import { formatPhoneNumber } from '../../../../utilities/Utils';
import { Prospect, FlatProspect } from '../../../../api/Prospect';
import {
    ProspectGridColumns,
    ProspectsGridColumns,
    TourGridColumns
} from '../../../../utilities/Enums';
import * as ReactDataGrid from 'react-data-grid';
import { Property } from '../../../../api/Property';
import { getProspectData } from '../../../../api/Prospect';
import LoadingOverlay from '../../../common/LoadingOverlay';
import { ToursGridContainer } from './toursGridContainer';
import { ProspectsGridContainer } from './prospectsGridContainer';
import { DashboardProspectPopup } from './dashboardProspectPopup';
import { DashboardProspectTourPopup } from './dashboardProspectTourPopup';
import BlockProspectModal from './blockProspectPopup';
import '../../../../styles/RecentActivity.css';

const standardDateFormat = 'M/D/YYYY';

enum RecentActivityType {
    Tours,
    Prospects
}

export enum ViewActivityType {
    BlockView,
    ProspectView
}

export type SortDirection = 'ASC' | 'DESC' | 'NONE';

export const useProspectTourSortState: (
    defaultSortDirection?: SortDirection,
    defaultSortColumn?: TourGridColumns
) => [
    TourGridColumns,
    SortDirection,
    (column: TourGridColumns, direction: SortDirection) => void
] = (defaultSortDirection, defaultSortColumn) => {
    const [sortColumn, setSortColumn] = useState<TourGridColumns>(
        defaultSortColumn || TourGridColumns.TourDate
    );

    const [sortDirection, setSortDirection] = useState<SortDirection>(
        defaultSortDirection || 'NONE'
    );

    const setBoth = (column: TourGridColumns, direction: SortDirection) => {
        if (direction === 'NONE') {
            setSortColumn(defaultSortColumn || column);
            setSortDirection(defaultSortDirection || 'NONE');
        } else {
            setSortColumn(column);
            setSortDirection(direction);
        }
    };

    return [sortColumn, sortDirection, setBoth];
};

export const useProspectsSortState: (
    defaultSortDirection?: SortDirection,
    defaultSortColumn?: ProspectsGridColumns
) => [
    ProspectsGridColumns,
    SortDirection,
    (column: ProspectsGridColumns, direction: SortDirection) => void
] = (defaultSortDirection, defaultSortColumn) => {
    const [sortColumn, setSortColumn] = useState<ProspectsGridColumns>(
        defaultSortColumn || ProspectsGridColumns.InitialDate
    );

    const [sortDirection, setSortDirection] = useState<SortDirection>(
        defaultSortDirection || 'NONE'
    );

    const setBoth = (column: ProspectsGridColumns, direction: SortDirection) => {
        if (direction === 'NONE') {
            setSortColumn(defaultSortColumn || column);
            setSortDirection(defaultSortDirection || 'NONE');
        } else {
            setSortColumn(column);
            setSortDirection(direction);
        }
    };

    return [sortColumn, sortDirection, setBoth];
};

export const useProspectSortState: (
    defaultSortDirection?: SortDirection,
    defaultSortColumn?: ProspectGridColumns
) => [
    ProspectGridColumns,
    SortDirection,
    (column: ProspectGridColumns, direction: SortDirection) => void
] = (defaultSortDirection, defaultSortColumn) => {
    const [sortColumn, setSortColumn] = useState<ProspectGridColumns>(
        defaultSortColumn || ProspectGridColumns.DateCreated
    );

    const [sortDirection, setSortDirection] = useState<SortDirection>(
        defaultSortDirection || 'NONE'
    );

    const setBoth = (column: ProspectGridColumns, direction: SortDirection) => {
        if (direction === 'NONE') {
            setSortColumn(defaultSortColumn || column);
            setSortDirection(defaultSortDirection || 'NONE');
        } else {
            setSortColumn(column);
            setSortDirection(direction);
        }
    };

    return [sortColumn, sortDirection, setBoth];
};

export const ProspectNameFormatter = (props: { value: string }) => (
    <div className="blue-prospect-name">{
        props.value.includes("blocked:")
            ?
            <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>{
                props.value.split("blocked:")[1]} 
                <svg style={{ marginLeft: '4px' }} width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.4688 13.4062L3.09375 5.03125C2.40625 6.03125 2 7.21875 2 8.5C2 11.8125 4.6875 14.5 8 14.5C9.28125 14.5 10.4688 14.0938 11.4688 13.4062ZM12.875 12C13.5625 11 14 9.8125 14 8.5C14 5.1875 11.3125 2.5 8 2.5C6.6875 2.5 5.5 2.9375 4.5 3.625L12.875 12ZM0 8.5C0 5.65625 1.5 3.03125 4 1.59375C6.46875 0.15625 9.5 0.15625 12 1.59375C14.4688 3.03125 16 5.65625 16 8.5C16 11.375 14.4688 14 12 15.4375C9.5 16.875 6.46875 16.875 4 15.4375C1.5 14 0 11.375 0 8.5Z" fill="#C00F0C"/>
                </svg>
            </span>
            :
            props.value
       }</div>
);

export const ProspectsColumns: {
    Name: ReactDataGrid.Column;
    Phone: ReactDataGrid.Column;
    Email: ReactDataGrid.Column;
    InitialDate: ReactDataGrid.Column;
    LastTourDate: ReactDataGrid.Column;
    LastTourPropertyName: ReactDataGrid.Column;
    Status: ReactDataGrid.Column;
    ApplicationStatus: ReactDataGrid.Column;
    TotalPropertiesVisited: ReactDataGrid.Column;
} = {
    Name: {
        key: ProspectsGridColumns.Name,
        name: 'Name',
        resizable: true,
        sortable: true,
        formatter: ProspectNameFormatter
    },
    Email: {
        key: ProspectsGridColumns.Email,
        name: 'Email',
        resizable: true,
        sortable: true
    },
    Phone: {
        key: ProspectsGridColumns.Phone,
        name: 'Phone Number',
        resizable: true,
        sortable: true
    },
    InitialDate: {
        key: ProspectsGridColumns.InitialDate,
        name: 'Initial Date',
        resizable: true,
        sortable: true
    },
    LastTourDate: {
        key: ProspectsGridColumns.LastTourDate,
        name: 'Last Tour Date',
        resizable: true,
        sortable: true
    },
    LastTourPropertyName: {
        key: ProspectsGridColumns.LastTourPropertyName,
        name: 'Property Name',
        resizable: true,
        sortable: true
    },
    Status: {
        key: ProspectsGridColumns.Status,
        name: 'Tour Status',
        resizable: true,
        sortable: true
    },
    ApplicationStatus: {
        key: ProspectsGridColumns.ApplicationStatus,
        name: 'App Status',
        resizable: true,
        sortable: true
    },
    TotalPropertiesVisited: {
        key: ProspectsGridColumns.TotalPropertiesVisited,
        name: 'Total Properties Visited',
        resizable: true,
        sortable: true
    }
};

export const ToursColumns: {
    Name: ReactDataGrid.Column;
    Phone: ReactDataGrid.Column;
    Email: ReactDataGrid.Column;
    PropertyName: ReactDataGrid.Column;
    Partner: ReactDataGrid.Column;
    TourCreationDate: ReactDataGrid.Column;
    TourDate: ReactDataGrid.Column;
    TourTime: ReactDataGrid.Column;
    TourEndTime: ReactDataGrid.Column;
    Status: ReactDataGrid.Column;
    
} = {
    Name: {
        key: TourGridColumns.Name,
        name: 'Name',
        resizable: true,
        sortable: true,
        formatter: ProspectNameFormatter
    },
    Phone: {
        key: TourGridColumns.Phone,
        name: 'Phone Number',
        resizable: true,
        sortable: true
    },
    Email: {
        key: TourGridColumns.Email,
        name: 'Email',
        resizable: true,
        sortable: true
    },
    PropertyName: {
        key: TourGridColumns.PropertyName,
        name: 'Property Name',
        resizable: true,
        sortable: true
    },
    Partner: {
        key: TourGridColumns.Partner,
        name: 'Partner',
        resizable: true,
        sortable: true
    },
    TourCreationDate: {
        key: TourGridColumns.TourCreationDate,
        name: 'Tour Creation Date',
        resizable: true,
        sortable: true
    },
    TourDate: {
        key: TourGridColumns.TourDate,
        name: 'Tour Date',
        resizable: true,
        sortable: true
    },
    TourTime: {
        key: TourGridColumns.TourTime,
        name: 'Tour Time',
        resizable: true,
        sortable: true
    },
    TourEndTime: {
        key: TourGridColumns.TourEndTime,
        name: 'Tour End Time',
        resizable: true,
        sortable: true
    },
    Status: {
        key: TourGridColumns.Status,
        name: 'Tour Status',
        resizable: true,
        sortable: true
    }
};

export const TourColumns: {
    Name: ReactDataGrid.Column;
    DateCreated: ReactDataGrid.Column;
    MoveInDate: ReactDataGrid.Column;
    TourStart: ReactDataGrid.Column;
    Status: ReactDataGrid.Column;
    Phone: ReactDataGrid.Column;
    Email: ReactDataGrid.Column;
    Partner: ReactDataGrid.Column;
    TourCreationDate: ReactDataGrid.Column;
    VirtualTourStatus: ReactDataGrid.Column;
    
} = {
    Name: {
        key: ProspectGridColumns.Name,
        name: 'Name',
        resizable: true,
        sortable: true,
        formatter: ProspectNameFormatter
    },
    DateCreated: {
        key: ProspectGridColumns.DateCreated,
        name: 'Initial Date',
        resizable: true,
        sortable: true
    },
    MoveInDate: {
        key: ProspectGridColumns.MoveInDate,
        name: 'Move In',
        resizable: true,
        sortable: true
    },
    TourStart: {
        key: ProspectGridColumns.TourStart,
        name: 'Tour Time',
        resizable: true,
        sortable: true
    },
    Status: {
        key: ProspectGridColumns.Status,
        name: 'Status',
        resizable: true,
        sortable: true
    },
    Phone: {
        key: ProspectGridColumns.Phone,
        name: 'Phone',
        resizable: true,
        sortable: true
    },
    Email: {
        key: ProspectGridColumns.Email,
        name: 'Email',
        resizable: true,
        sortable: true
    },
    Partner: {
        key: ProspectGridColumns.Partner,
        name: 'Partner',
        resizable: true,
        sortable: true
    },
    TourCreationDate: {
        key: ProspectGridColumns.TourCreationDate,
        name: 'Tour Creation Date',
        resizable: true,
        sortable: true
    },
    VirtualTourStatus: {
        key: ProspectGridColumns.VirtualTourStatus,
        name: 'Status',
        resizable: true,
        sortable: true
    }
};

export interface ProspectsGridProps {
    prospects: prospectApi.FlatProspect[];
    setSelectedProspectId: (prospectToken: string | null) => void;
}

export interface ProspectsGridContainerProps {
    prospects: prospectApi.ProspectInfo[];
    typeCall: (type: ViewActivityType, prospectId: string) => void;
}

export interface ToursGridConatinerProps {
    prospects: prospectApi.ProspectTourInfo[];
    setSelectedTourId: (tourId: string | null) => void;
}

interface RecentActivityProps {
    prospects: prospectApi.ProspectInfo[];
    tourProspects: prospectApi.ProspectTourInfo[];
    loading: boolean;
    startDate: moment.Moment;
    endDate: moment.Moment;
    propertyId: number | null;
    property: Property | undefined;
    updateSelectedProspect: (newProspect: Prospect | null, cancelled: boolean) => void;
    addExcludedProspect?: (prospect: Prospect) => void;
}

export const getGridProspectTourDisplay = (prospect: prospectApi.ProspectTourInfo) => ({
    ...prospect,
    name: `${prospect.firstName} ${prospect.lastName}`,
    phone: prospect.phoneNumber!="" && prospect.phoneNumber!=null?  formatPhoneNumber(prospect.phoneNumber):"",
    email: prospect.email,
    propertyName: prospect.propertyName,
    partnerName: prospect.partner,
    //TODO: NEED TO CHECK THIS
    tourCreationDate: prospect.tourCreationDate!="" && prospect.tourCreationDate!=null? moment(prospect.tourCreationDate).format(standardDateFormat):"",
    tourDate: prospect.tourStartDate !="" && prospect.tourStartDate !=null? moment(prospect.tourStartDate ).format(standardDateFormat):"",
    tourTime: prospect.tourStartTime,
    tourEndTime: prospect.tourEndTime,
    tourStatus: prospect.tourStatus
});

export const getGridProspectInfoDisplay = (prospect: prospectApi.ProspectInfo) => ({
    ...prospect,
    name: prospect.blockedStatus? `blocked:${prospect.firstName} ${prospect.lastName}` :`${prospect.firstName} ${prospect.lastName}`,
    email: `${prospect.email}`,
    phone: prospect.phoneNumber!="" && prospect.phoneNumber!=null?  formatPhoneNumber(prospect.phoneNumber):"",
    initialDate: prospect.initialDate!="" && prospect.initialDate!=null? moment(prospect.initialDate).format(standardDateFormat):"",
    lastTourDate: prospect.lastTourDate!="" && prospect.lastTourDate!=null? moment(prospect.lastTourDate).format(standardDateFormat):"",
    lastTourDatePropertyName: `${prospect.lastTourDatePropertyName}`,
    tourStatus: `${prospect.tourStatus}`,
    applicationStatus: `${prospect.applicationStatus}`,
    totalPropertiesVisited: `${prospect.totalPropertiesVisited}`
});

export const getGridProspectDisplay = (prospect: FlatProspect) => ({
    ...prospect,
    name: `${prospect.firstName} ${prospect.lastName}`,
    dateCreated: prospect.dateCreated!="" && prospect.dateCreated!=null? moment(prospect.dateCreated).format(standardDateFormat):"",
    moveInDate: prospect.moveInDate!="" && prospect.moveInDate!=null? moment(prospect.moveInDate).format(standardDateFormat):"",
    phone: prospect.phone!="" && prospect.phone!=null?  formatPhoneNumber(prospect.phone):"",
    tourStart:
        prospect.tourInterval &&
        prospect.tourInterval.startDateTime &&
        `${moment(prospect.tourInterval.startDateTime).format(`${standardDateFormat}`)} ${moment(
            prospect.tourInterval.startDateTime
        ).format(`h:mm A`)}`,
     //   partnerName: JSON.stringify(prospect)
       partnerName: prospect.partnerName
        
});

export const RecentActivityWidget = ({
    prospects,
    tourProspects,
    loading,
    startDate,
    endDate,
    propertyId,
    property,
    updateSelectedProspect,
    addExcludedProspect
}: RecentActivityProps) => {
    // set up local state variables for use with the controls in this widget via the 'useState' hook
    const [selectedTab, setSelectedTab] = useState<RecentActivityType>(
        RecentActivityType.Prospects
    );

    const [selectedProspectId, setSelectedProspectId] = useState<string | null>(null);
    const [prospect, setProspect] = useState();
    const [loadProspect, setLoadProspect] = useState(false);

    //from here
    const [tour, setTour] = useState();
    const [selectedTourId, setSelectedTourId] = useState<string | null>(null);
    const [openBlockProspect, setBlockProspect] = useState(false);
    const [blockMessage, setBlockMessage] = useState(false);

    const getTourData = async () => {
        let result = await prospectApi.getTourInfo(selectedTourId);
        if(result.success && result.data){
            setTour(result.data)
            console.log(result.data, 'result.data')
            console.log(tour)
        }
    }

    useEffect(()=>{
        getTourData()
    },[selectedTourId])

    useEffect(() => {
        if (blockMessage) {
          const timer = setTimeout(() => {
            setBlockMessage(false);
          }, 3000); // Change state after 3 seconds
      
          return () => clearTimeout(timer); // Cleanup function
        }
        return undefined; // Ensure all code paths return a value
      }, [blockMessage]);

    const getProspectInfo = async (prospectId: string) => {
       
        setLoadProspect(true)
        setSelectedProspectId(prospectId)
        if(prospectId){
            let result = await getProspectData({
                "PropertyIds": [propertyId],
                "ProspectId": prospectId
            });
            setProspect(result.data)
        }
        setLoadProspect(false)

    };

    return (
        <DashboardPanel isDouble={true}>
             <div className="tabs-container">
                <div className="tabs-header">
                    <button
                        className={selectedTab === RecentActivityType.Prospects ? 'tab active' : 'tab'}
                        onClick={() => setSelectedTab(RecentActivityType.Prospects)}
                    >
                        Prospect
                    </button>
                    <button
                        className={selectedTab === RecentActivityType.Tours ? 'tab active' : 'tab'}
                        onClick={() => setSelectedTab(RecentActivityType.Tours)}
                    >
                        Tour
                    </button>
                </div>
            </div>
            {loading ? (
                <LoadingOverlay show={true} inBootstrapColumn={false} />
            ) :
            (prospects.length > 0 && selectedTab === RecentActivityType.Prospects  ? 
                    <ProspectsGridContainer
                        prospects={prospects}
                        typeCall={(type: ViewActivityType, prospectId: string)=>{
                            if(type === ViewActivityType.BlockView){
                                setBlockProspect(true)
                                setSelectedProspectId(prospectId)
                            }else{
                                getProspectInfo(prospectId)
                            }
                        }}
                    />
                  : tourProspects.length > 0 && selectedTab === RecentActivityType.Tours ?
                    <ToursGridContainer
                        prospects={tourProspects}
                        setSelectedTourId={setSelectedTourId}
                    /> : 
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                    No activity for this time period
                  </div>
                  
            )}
            { selectedTab === RecentActivityType.Prospects && selectedProspectId && !openBlockProspect && !loadProspect && <DashboardProspectPopup
                setSelectedProspectToken={setSelectedProspectId}
                prospectToken={selectedProspectId}
                prospect={prospect}
                propertyId={propertyId}
            />}
             { selectedTab === RecentActivityType.Tours && tour && selectedTourId && <DashboardProspectTourPopup
                setSelectedTab={setSelectedTab}
                setSelectedProspectId={setSelectedProspectId}
                setSelectedTourId={setSelectedTourId}
                tourId={selectedTourId}
                tourInfo={tour}
            />}
            {loadProspect &&
                <LoadingOverlay show={true} inBootstrapColumn={false} />}
            {
                openBlockProspect && selectedProspectId && !loadProspect &&<BlockProspectModal isOpen={openBlockProspect} prospect={prospect} onClose={function (): void {
                    setSelectedProspectId('');
                    setBlockProspect(false);
                } } onBlock={function (reason: string): void {
                    setSelectedProspectId('');
                    setBlockProspect(false);
                    setBlockMessage(true)
                } }  prospectUserId={selectedProspectId} propertyId={propertyId} />
            }
            { blockMessage &&<div 
                style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    backgroundColor: '#FDE8E8',
                    color: '#D32F2F',
                    padding: '15px 25px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                }}
            >
                <span style={{ marginRight: '10px' }}>🚫</span>
                 Prospect Successfully Blocked
            </div>}
        </DashboardPanel>
    );
};



// WEBPACK FOOTER //
// ./src/components/pages/admin/dashboardWidgets/recentActivity.tsx